import { useAuthStore } from '../store/authStore';
import { useServerHealthStore } from '../store/serverHealthStore';
import { wsClient } from './websocket';

const CONNECT_TIMEOUT_MS = 8_000;
const SYNC_TIMEOUT_MS = 12_000;

type MultiServerSyncResult = {
  completed: boolean;
  interrupted: boolean;
  perServer: Array<{
    serverId: string;
    identityId: string;
    ok: boolean;
    error?: string;
  }>;
};

let inFlight: Promise<MultiServerSyncResult> | null = null;

function waitForWsEvent(type: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const unsubscribe = wsClient.on(type, () => {
      cleanup();
      resolve();
    });

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timeout waiting for WS event: ${type}`));
    }, timeoutMs);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  });
}

async function syncCurrentServerOnce(): Promise<void> {
  // Prepare listeners BEFORE waiting for `registered` so we don't miss fast responses.
  const syncResult = waitForWsEvent('message:sync-result', SYNC_TIMEOUT_MS).catch(() => undefined);
  const syncStatus = waitForWsEvent('message:sync-status-result', SYNC_TIMEOUT_MS).catch(() => undefined);
  const systemSyncResult = waitForWsEvent('system:sync-result', SYNC_TIMEOUT_MS).catch(() => undefined);
  const callHistorySyncResult = waitForWsEvent('call-history:sync-result', SYNC_TIMEOUT_MS).catch(() => undefined);

  // Wait until the socket is authenticated for the currently selected identity.
  await waitForWsEvent('registered', CONNECT_TIMEOUT_MS);

  // The messaging subsystem sends `message:sync` + `message:sync-status` on `registered`.
  // The system events subsystem sends `system:sync` on `registered`.
  // We just wait for the corresponding results (best-effort).
  await Promise.all([syncResult, syncStatus, systemSyncResult, callHistorySyncResult]);
}

/**
 * Best-effort sequential sync from all known servers/identities.
 * Returns whether the run completed or was interrupted.
 */
export function syncAllServersSequentiallyWithResult(options?: {
  shouldContinue?: () => boolean;
  shouldRestoreOriginalDevice?: () => boolean;
}): Promise<MultiServerSyncResult> {
  if (inFlight) return inFlight;

  const shouldContinue = options?.shouldContinue || (() => true);
  const shouldRestoreOriginalDevice = options?.shouldRestoreOriginalDevice || (() => true);

  inFlight = (async (): Promise<MultiServerSyncResult> => {
    const state = useAuthStore.getState();
    const originalDeviceId = state.currentDeviceId;

    const identities = Object.values(state.identities)
      .filter((i) => !!i.serverUrl)
      .sort((a, b) => (a.serverId || '').localeCompare(b.serverId || ''));

    if (identities.length <= 1) {
      return { completed: true, interrupted: false, perServer: [] };
    }

    let interrupted = false;
    const perServer: MultiServerSyncResult['perServer'] = [];

    try {
      for (const identity of identities) {
        if (!shouldContinue()) {
          interrupted = true;
          break;
        }

        const nextState = useAuthStore.getState();
        const device = Object.values(nextState.devices).find((d) => d.identityId === identity.identityId);
        if (!device) continue;

        try {
          if (nextState.currentDeviceId !== device.deviceId) {
            nextState.setCurrentDevice(device.deviceId);
          }

          await syncCurrentServerOnce();
          perServer.push({
            serverId: identity.serverId,
            identityId: identity.identityId,
            ok: true
          });
          useServerHealthStore.getState().markSyncSuccess(identity.serverId);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sync failed';
          perServer.push({
            serverId: identity.serverId,
            identityId: identity.identityId,
            ok: false,
            error: message
          });
          useServerHealthStore.getState().markSyncFailure(identity.serverId, message);
          // Best-effort: continue with the remaining circles.
          // Next iteration will switch away which also stops reconnect attempts.
        }
      }
    } finally {
      // Restore the user's previously selected server/device unless the user
      // navigated into an explicit chat while this background sweep was running.
      try {
        const finalState = useAuthStore.getState();
        if (originalDeviceId && shouldRestoreOriginalDevice() && finalState.currentDeviceId !== originalDeviceId) {
          finalState.setCurrentDevice(originalDeviceId);
        }
      } catch {
        // ignore
      }
    }

    return { completed: !interrupted, interrupted, perServer };
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}
